import React, { useState, useMemo } from "react";
import { Link, NavLink } from "react-router-dom";
import axios from "axios";
import Spinner from "react-bootstrap/Spinner";
import { ToastContainer, toast } from "react-toastify";
import FeeSystemMenu from "./FeeSystemMenu";
import config from "../../Config";
import BanksDropDown from "../Shared/BanksDropDown";
import moment from "moment";

const MONTHS = [
  { value: 1, label: "Jan" }, { value: 2, label: "Feb" }, { value: 3, label: "Mar" },
  { value: 4, label: "Apr" }, { value: 5, label: "May" }, { value: 6, label: "Jun" },
  { value: 7, label: "Jul" }, { value: 8, label: "Aug" }, { value: 9, label: "Sep" },
  { value: 10, label: "Oct" }, { value: 11, label: "Nov" }, { value: 12, label: "Dec" },
];

const DuplicatePaymentReceived = () => {
  const now = new Date();
  const [AccessKey] = useState(localStorage.getItem("AccessKey"));
  const [UserID] = useState(localStorage.getItem("ID"));
  const [MemberType] = useState(localStorage.getItem("MemberType"));

  const [RegistrationNo, setRegistrationNo] = useState("");
  const [StudentId, setStudentId] = useState("");
  const [studentInfo, setStudentInfo] = useState(null);
  const [preview, setPreview] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [processLoading, setProcessLoading] = useState(false);

  const [duplicateAmount, setDuplicateAmount] = useState("");
  const [depositDate, setDepositDate] = useState(moment().format("YYYY-MM-DD"));
  const [bankId, setBankId] = useState(0);
  const [receiptNo, setReceiptNo] = useState("");
  const [benchNo, setBenchNo] = useState("1");
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [fYear, setFYear] = useState(now.getFullYear());
  const [dueDay, setDueDay] = useState(25);
  const [notes, setNotes] = useState("");
  const [selectedUnpaid, setSelectedUnpaid] = useState([]);

  const [lastResult, setLastResult] = useState(null);

  const dueDateString = useMemo(() => {
    const daysInMonth = new Date(fYear, selectedMonth, 0).getDate();
    const day = Math.min(Math.max(1, dueDay), daysInMonth);
    return `${fYear}-${String(selectedMonth).padStart(2, "0")}-${String(day).padStart(2, "0")}T00:00:00`;
  }, [fYear, selectedMonth, dueDay]);

  const loadPreview = async (studentIdValue) => {
    const resp = await axios({
      method: "post",
      url: config.base_url + "ADLedger/GetDuplicatePaymentPreview",
      headers: { "Content-Type": "application/json" },
      timeout: 120000,
      data: {
        AccessKey,
        UserID,
        MemberType,
        Student_ID: studentIdValue,
      },
    });
    if (resp.data.status_code === 1 && resp.data.Preview) {
      setPreview(resp.data.Preview);
      setSelectedUnpaid([]);
    } else {
      setPreview(null);
      toast.error(resp.data.status_message || "Could not load student fee summary.", {
        position: toast.POSITION.TOP_RIGHT,
      });
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setStudentInfo(null);
    setPreview(null);
    setLastResult(null);

    if (!RegistrationNo && !StudentId) {
      toast.error("Enter registration number or student ID.", { position: toast.POSITION.TOP_RIGHT });
      return;
    }

    setSearchLoading(true);
    try {
      let studentIdValue = StudentId;
      if (!studentIdValue && RegistrationNo) {
        const regResp = await axios({
          method: "post",
          url: config.base_url + "Student/GetStudentByRegistrationNo",
          headers: { "Content-Type": "application/json" },
          data: { AccessKey, UserID, MemberType, Registration_No: RegistrationNo },
        });
        if (regResp.data.status_code !== 1 || !regResp.data.Student) {
          toast.error(regResp.data.status_message || "Student not found.", {
            position: toast.POSITION.TOP_RIGHT,
          });
          setSearchLoading(false);
          return;
        }
        studentIdValue = regResp.data.Student.Student_ID;
      }

      const detailResp = await axios({
        method: "post",
        url: config.base_url + "Student/GetStudentByID",
        headers: { "Content-Type": "application/json" },
        data: { AccessKey, UserID, MemberType, ID: studentIdValue },
      });

      if (detailResp.data.status_code === 1 && detailResp.data.Student) {
        const s = detailResp.data.Student;
        setStudentInfo(s);
        await loadPreview(s.Student_ID);
        toast.success("Student loaded.", { position: toast.POSITION.TOP_RIGHT });
      } else {
        toast.error(detailResp.data.status_message || "Student not found.", {
          position: toast.POSITION.TOP_RIGHT,
        });
      }
    } catch {
      toast.error("Network error.", { position: toast.POSITION.TOP_RIGHT });
    } finally {
      setSearchLoading(false);
    }
  };

  const toggleUnpaidChallan = (challanNo) => {
    const cn = String(challanNo || "").trim();
    setSelectedUnpaid((prev) =>
      prev.includes(cn) ? prev.filter((x) => x !== cn) : [...prev, cn]
    );
  };

  const selectedUnpaidTotal = useMemo(() => {
    if (!preview?.UnpaidChallans) return 0;
    return preview.UnpaidChallans.filter((c) =>
      selectedUnpaid.includes(String(c.Challan_No || "").trim())
    ).reduce((sum, c) => sum + (Number(c.PendingAmount) || 0), 0);
  }, [preview, selectedUnpaid]);

  const handleProcess = async (e) => {
    e.preventDefault();
    setLastResult(null);

    if (!studentInfo) {
      toast.error("Search and select a student first.", { position: toast.POSITION.TOP_RIGHT });
      return;
    }
    if (!studentInfo.Registration_No) {
      toast.error("Student must have a registration number.", { position: toast.POSITION.TOP_RIGHT });
      return;
    }

    const amount = parseFloat(duplicateAmount);
    if (!duplicateAmount || isNaN(amount) || amount <= 0) {
      toast.error("Enter a valid duplicate payment amount.", { position: toast.POSITION.TOP_RIGHT });
      return;
    }
    if (!bankId || bankId <= 0) {
      toast.error("Select bank.", { position: toast.POSITION.TOP_RIGHT });
      return;
    }
    if (!receiptNo || parseInt(receiptNo, 10) < 1) {
      toast.error("Enter a valid receipt number.", { position: toast.POSITION.TOP_RIGHT });
      return;
    }
    if (!depositDate) {
      toast.error("Enter deposit date.", { position: toast.POSITION.TOP_RIGHT });
      return;
    }

    if (
      selectedUnpaid.length > 0 &&
      selectedUnpaidTotal > amount + 0.01
    ) {
      toast.error(
        "Selected unpaid challans total exceeds duplicate amount. Reduce selection or increase amount.",
        { position: toast.POSITION.TOP_RIGHT }
      );
      return;
    }

    if (
      !window.confirm(
        "Record duplicate payment?\n\nThis will:\n1) Create and pay an advance fee challan\n2) Add amount to advance balance\n3) Settle selected unpaid challans from that advance (if any)\n\nContinue?"
      )
    ) {
      return;
    }

    setProcessLoading(true);
    try {
      const resp = await axios({
        method: "post",
        url: config.base_url + "ADLedger/ProcessDuplicatePaymentReceived",
        headers: { "Content-Type": "application/json" },
        timeout: 180000,
        data: {
          AccessKey,
          UserID,
          MemberType,
          Student_ID: studentInfo.Student_ID,
          Registration_No: studentInfo.Registration_No,
          Amount: amount,
          AccountLevel: bankId,
          ReceiptNo: String(receiptNo),
          BenchNo: parseInt(benchNo, 10) || 1,
          StartDate: `${depositDate}T00:00:00`,
          SelectedMonth: selectedMonth,
          FYear: fYear,
          Challan_DueDate: dueDateString,
          Challan_Nos: selectedUnpaid.length > 0 ? selectedUnpaid : null,
          Remarks: notes,
        },
      });

      if (resp.data.status_code === 1) {
        setLastResult(resp.data.Result || null);
        toast.success(resp.data.status_message, { position: toast.POSITION.TOP_RIGHT });
        await loadPreview(studentInfo.Student_ID);
        setDuplicateAmount("");
        setSelectedUnpaid([]);
      } else {
        toast.error(resp.data.status_message || "Process failed.", {
          position: toast.POSITION.TOP_RIGHT,
        });
      }
    } catch {
      toast.error("Network error.", { position: toast.POSITION.TOP_RIGHT });
    } finally {
      setProcessLoading(false);
    }
  };

  const fetchNextReceipt = () => {
    if (!depositDate || !benchNo) return;
    axios({
      method: "post",
      url: config.base_url + "ScrollEntry/GetNextReceiptNumber",
      headers: { "Content-Type": "application/json" },
      data: {
        AccessKey,
        UserID,
        MemberType,
        BenchNumber: parseInt(benchNo, 10) || 1,
        Dated: `${depositDate}T00:00:00`,
      },
    }).then((resp) => {
      if (resp.data.status_code === 1 && resp.data.receiptNumber) {
        setReceiptNo(String(resp.data.receiptNumber));
      }
    });
  };

  return (
    <>
      <FeeSystemMenu />
      <ToastContainer />
      <div className="page-head">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-8">
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item"><Link to="/Dashboard">Dashboard</Link></li>
                  <li className="breadcrumb-item"><Link to="/FeeSystem/FeeSystemDashboard">Fee System</Link></li>
                  <li className="breadcrumb-item active" aria-current="page">Duplicate Payment Received</li>
                </ol>
              </nav>
              <div className="page-title">Duplicate Payment Received</div>
            </div>
          </div>
        </div>
      </div>

      <div className="page-content">
        <div className="container">
          <div className="card mb-3">
            <div className="card-body">
              <p className="text-muted mb-4">
                Use when a student paid the same fee twice (e.g. bank and Kuickpay). The system will create an{" "}
                <strong>advance fee challan</strong>, record a <strong>bank scroll entry</strong> on the deposit date
                (marked paid), add the amount to <strong>advance balance</strong>, and optionally settle other unpaid
                challans from that advance using the standard advance adjustment bank.
              </p>

              <form onSubmit={handleSearch} className="mb-4">
                <div className="row g-3 align-items-end">
                  <div className="col-md-3">
                    <label className="form-label">Registration No</label>
                    <input
                      type="text"
                      className="form-control"
                      value={RegistrationNo}
                      onChange={(e) => setRegistrationNo(e.target.value)}
                    />
                  </div>
                  <div className="col-md-1 text-center text-muted">OR</div>
                  <div className="col-md-3">
                    <label className="form-label">Student ID</label>
                    <input
                      type="number"
                      className="form-control"
                      value={StudentId}
                      onChange={(e) => setStudentId(e.target.value)}
                    />
                  </div>
                  <div className="col-md-3">
                    <button type="submit" className="button button-primary w-100" disabled={searchLoading}>
                      {searchLoading ? <Spinner animation="border" size="sm" /> : "Search Student"}
                    </button>
                  </div>
                </div>
              </form>

              {studentInfo && (
                <div className="alert alert-light border">
                  <strong>{studentInfo.Student_Name}</strong> — Reg: {studentInfo.Registration_No}
                  {preview && (
                    <span className="ms-3 text-muted">
                      Existing advance balance: Rs {Number(preview.TotalRemainingAdvance || 0).toLocaleString()}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          {studentInfo && studentInfo.Registration_No && (
            <form onSubmit={handleProcess}>
              <div className="card mb-3">
                <div className="card-body">
                  <h6 className="mb-3">1. Duplicate payment details</h6>
                  <div className="row g-3">
                    <div className="col-md-3">
                      <label className="form-label">Duplicate amount (Rs)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        className="form-control"
                        value={duplicateAmount}
                        onChange={(e) => setDuplicateAmount(e.target.value)}
                        required
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Deposit date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={depositDate}
                        onChange={(e) => {
                          setDepositDate(e.target.value);
                          setReceiptNo("");
                        }}
                        required
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Bank</label>
                      <select
                        className="form-select"
                        value={bankId}
                        onChange={(e) => setBankId(parseInt(e.target.value, 10) || 0)}
                        required
                      >
                        <option value="0">Select bank</option>
                        <BanksDropDown />
                      </select>
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">Bench</label>
                      <input
                        type="number"
                        min="1"
                        className="form-control"
                        value={benchNo}
                        onChange={(e) => setBenchNo(e.target.value)}
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Receipt no</label>
                      <div className="input-group">
                        <input
                          type="number"
                          min="1"
                          className="form-control"
                          value={receiptNo}
                          onChange={(e) => setReceiptNo(e.target.value)}
                          required
                        />
                        <button
                          type="button"
                          className="button button-white"
                          onClick={fetchNextReceipt}
                          title="Get next receipt for bench and date"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Notes (optional)</label>
                      <input
                        type="text"
                        className="form-control"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="e.g. Paid bank and Kuickpay for June challan"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="card mb-3">
                <div className="card-body">
                  <h6 className="mb-3">2. Advance challan (same as Generate Advance Challan)</h6>
                  <div className="row g-3">
                    <div className="col-md-2">
                      <label className="form-label">Month</label>
                      <select
                        className="form-select"
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(Number(e.target.value))}
                      >
                        {MONTHS.map((m) => (
                          <option key={m.value} value={m.value}>{m.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">Year</label>
                      <select
                        className="form-select"
                        value={fYear}
                        onChange={(e) => setFYear(Number(e.target.value))}
                      >
                        {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((y) => (
                          <option key={y} value={y}>{y}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">Due day</label>
                      <input
                        type="number"
                        min={1}
                        max={31}
                        className="form-control"
                        value={dueDay}
                        onChange={(e) => setDueDay(Math.min(31, Math.max(1, Number(e.target.value) || 1)))}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="card mb-3">
                <div className="card-body">
                  <h6 className="mb-2">3. Other unpaid challans (optional)</h6>
                  <p className="text-muted small mb-3">
                    If the duplicate payment should also clear another unpaid challan, tick it below. Settlement uses
                    advance adjustment (bank id 3). Total selected pending must not exceed duplicate amount.
                  </p>
                  {preview?.UnpaidChallans?.length > 0 ? (
                    <>
                      <div className="table-responsive">
                        <table className="table table-theme table-sm">
                          <thead>
                            <tr>
                              <th></th>
                              <th>Challan No</th>
                              <th>Month/Year</th>
                              <th>Type</th>
                              <th>Status</th>
                              <th className="text-end">Pending (Rs)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {preview.UnpaidChallans.map((row) => {
                              const cn = String(row.Challan_No || "").trim();
                              return (
                                <tr key={cn}>
                                  <td>
                                    <input
                                      type="checkbox"
                                      className="form-check-input"
                                      checked={selectedUnpaid.includes(cn)}
                                      onChange={() => toggleUnpaidChallan(cn)}
                                    />
                                  </td>
                                  <td>{cn}</td>
                                  <td>{row.Month}/{row.Year}</td>
                                  <td>{row.Reference_ID_Type}</td>
                                  <td>{row.VoucherStatus}</td>
                                  <td className="text-end">{Number(row.PendingAmount).toLocaleString()}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      {selectedUnpaid.length > 0 && (
                        <p className="mb-0">
                          <strong>Selected pending total:</strong> Rs {selectedUnpaidTotal.toLocaleString()}
                          {" / "}
                          Duplicate amount: Rs {Number(duplicateAmount || 0).toLocaleString()}
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="text-muted mb-0">No other unpaid challans for this student.</p>
                  )}
                </div>
              </div>

              <div className="card mb-3">
                <div className="card-body">
                  <button
                    type="submit"
                    className="button button-primary"
                    disabled={processLoading}
                  >
                    {processLoading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Processing…
                      </>
                    ) : (
                      "Record duplicate payment"
                    )}
                  </button>
                </div>
              </div>
            </form>
          )}

          {lastResult && (
            <div className="card">
              <div className="card-body alert alert-success mb-0">
                <h6 className="alert-heading">Last adjustment completed</h6>
                <p className="mb-1"><strong>Advance challan:</strong> {lastResult.AdvanceChallan_No}</p>
                <p className="mb-1"><strong>Scroll entry ID:</strong> {lastResult.ScrollEntry_Id}</p>
                <p className="mb-1"><strong>Advance payment ID:</strong> {lastResult.AdvancePayment_Id}</p>
                <p className="mb-1"><strong>Amount recorded:</strong> Rs {Number(lastResult.AmountRecorded).toLocaleString()}</p>
                {lastResult.SettledChallanNos?.length > 0 && (
                  <p className="mb-1">
                    <strong>Settled challans:</strong> {lastResult.SettledChallanNos.join(", ")}
                  </p>
                )}
                <p className="mb-3">
                  <strong>Remaining advance:</strong> Rs {Number(lastResult.AdvanceRemainingAfterSettlement || 0).toLocaleString()}
                </p>
                <NavLink
                  to={`/students/PrintVoucher?ChallanNos=${lastResult.AdvanceChallan_No}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="button button-primary"
                >
                  Print advance challan
                </NavLink>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default DuplicatePaymentReceived;
